/**
 * @license
 * Copyright (c) 2021-present, FriendliAI Inc. All rights reserved.
 */

import { Center, rem, useMantineTheme } from '@mantine/core';
import {
  RiAlertFill,
  RiCircleFill,
  RiLoader2Line,
  RiLoader4Line,
  RiMoonFill,
  RiStopCircleFill,
} from '@remixicon/react';
import { useTranslation } from 'react-i18next';

import {
  DedicatedEndpointInitializingPhaseStep,
  DedicatedEndpointStatus,
} from '@fai/friendli-suite/generated/client/graphql';
import { rotateIcon } from '@fai/ui/f.css';

import type { EndpointInitializingStatusVariants, EndpointStatusVariants } from './model';

export const useEndpointStatusVariants = (): EndpointStatusVariants => {
  const { t } = useTranslation();
  const theme = useMantineTheme();

  return {
    [DedicatedEndpointStatus.Running]: {
      icon: (
        <Center h={rem(16)} w={rem(16)}>
          <RiCircleFill size={8} />
        </Center>
      ),
      content: t('dedicated_endpoints.project.endpoint_status_running'),
      color: theme.pf.color.fg.apigreen.base,
      backgroundColor: theme.pf.color.bg.success.default.base,
      borderColor: theme.colors.lime[4],
    },
    [DedicatedEndpointStatus.Sleeping]: {
      icon: <RiMoonFill size={16} />,
      content: t('dedicated_endpoints.project.endpoint_status_sleeping'),
      color: theme.pf.color.fg.neutral.default,
      backgroundColor: theme.pf.color.bg.neutral.subtle.hover,
      borderColor: theme.colors.gray[4],
    },
    [DedicatedEndpointStatus.Initializing]: {
      icon: <RiLoader4Line className={rotateIcon} size={16} />,
      content: t('dedicated_endpoints.project.endpoint_status_initializing'),
      color: theme.pf.color.fg.warning.default.press,
      backgroundColor: theme.pf.color.bg.warning.default.base,
      borderColor: theme.colors.yellow[4],
    },
    [DedicatedEndpointStatus.Awaking]: {
      icon: <RiLoader4Line className={rotateIcon} size={16} />,
      content: t('dedicated_endpoints.project.endpoint_status_awaking'),
      color: theme.pf.color.fg.warning.default.press,
      backgroundColor: theme.pf.color.bg.warning.default.base,
      borderColor: theme.colors.yellow[4],
    },
    [DedicatedEndpointStatus.Terminating]: {
      icon: <RiLoader4Line className={rotateIcon} size={16} />,
      content: t('dedicated_endpoints.project.endpoint_status_terminating'),
      color: theme.pf.color.fg.neutral.default,
      backgroundColor: theme.pf.color.bg.neutral.subtle.hover,
      borderColor: theme.colors.gray[4],
    },
    [DedicatedEndpointStatus.Terminated]: {
      icon: <RiStopCircleFill size={16} />,
      content: t('dedicated_endpoints.project.endpoint_status_terminated'),
      color: theme.pf.color.fg.neutral.default,
      backgroundColor: theme.pf.color.bg.neutral.subtle.hover,
      borderColor: theme.colors.gray[4],
    },
    [DedicatedEndpointStatus.Failed]: {
      icon: <RiAlertFill size={16} />,
      content: t('dedicated_endpoints.project.endpoint_status_failed'),
      color: theme.pf.color.fg.danger.default.hover,
      backgroundColor: theme.pf.color.bg.danger.default.base,
      borderColor: theme.colors.red[3],
    },
    [DedicatedEndpointStatus.Updating]: {
      icon: <RiLoader4Line className={rotateIcon} size={16} />,
      content: t('dedicated_endpoints.project.endpoint_status_updating'),
      color: theme.pf.color.fg.warning.default.press,
      backgroundColor: theme.pf.color.bg.warning.default.base,
      borderColor: theme.colors.yellow[4],
    },
    [DedicatedEndpointStatus.Ready]: {
      icon: null,
      content: t('dedicated_endpoints.project.endpoint_status_ready'),
      color: theme.pf.color.fg.cloudblue.default.base,
      backgroundColor: theme.pf.color.bg.cloudblue.default.base,
      borderColor: theme.colors.blue[4],
    },

    // TODO(mj): DEPRECATED. WILL BE REMOVED IN FUTURE RELEASE.
    [DedicatedEndpointStatus.Stopping]: {
      icon: <RiLoader4Line className={rotateIcon} size={16} />,
      content: t('dedicated_endpoints.project.endpoint_status_terminating'),
      color: theme.pf.color.fg.neutral.default,
      backgroundColor: theme.pf.color.bg.neutral.subtle.hover,
      borderColor: theme.colors.gray[4],
    },
    [DedicatedEndpointStatus.Unknown]: {
      icon: null,
      content: t('dedicated_endpoints.project.endpoint_status_unknown'),
      color: theme.pf.color.fg.danger.default.hover,
      backgroundColor: theme.pf.color.bg.danger.default.base,
      borderColor: theme.colors.red[3],
    },
  };
};

export const useEndpointInitializingPhaseVariants = (): EndpointInitializingStatusVariants => {
  const { t } = useTranslation();

  return {
    [DedicatedEndpointInitializingPhaseStep.DownloadingModel]: {
      content: t('dedicated_endpoints.project.downloading_model'),
    },
    [DedicatedEndpointInitializingPhaseStep.EngineInitializing]: {
      content: t('dedicated_endpoints.project.engine_initializing'),
    },
    [DedicatedEndpointInitializingPhaseStep.RequestingVirtualMachine]: {
      content: t('dedicated_endpoints.project.requesting_virtual_machine'),
    },
  };
};
